import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import { PlayingCard } from '../../../poker-ui/PlayingCard';
import { belotCardIdToPlaying } from '../../../game/belotArmenianCards';
import {
  isCarreRevealCandidate,
  isTerzRevealCandidate,
  revealChipTextFromOpts,
} from '../belotComboLabelUi';

const REVEAL_Z = 225;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Центр элемента в координатах вьюпорта или fallback.
 * @param {HTMLElement|null|undefined} el
 * @param {{ x: number, y: number }} fallback
 */
function anchorCenter(el, fallback) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return fallback;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 && r.height <= 0) return fallback;
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/**
 * Старт: смещение от центра якоря героя наружу относительно центра экрана (Candywings-style).
 * Fallback без якоря: низ экрана по центру — «вылет снизу».
 * @param {{ x: number, y: number }} heroCenter
 */
/**
 * @param {{ x: number, y: number }} heroCenter
 * @param {{ carre?: boolean, seatCombo?: boolean }} [opts]
 */
function startLaunchPoint(heroCenter, opts = {}) {
  const carre = opts.carre === true;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 700;
  const cx = vw / 2;
  const cy = vh / 2;

  const anchor =
    heroCenter && Number.isFinite(heroCenter.x) && Number.isFinite(heroCenter.y)
      ? heroCenter
      : { x: cx, y: vh * 0.92 };

  let dx = anchor.x - cx;
  let dy = anchor.y - cy;
  const len = Math.hypot(dx, dy) || 1;
  dx /= len;
  dy /= len;
  const seatCombo = opts.seatCombo === true;
  let push = carre ? 168 : seatCombo ? 126 : 110;
  if (carre) {
    const minOut = Math.max(vw, vh) * 0.4;
    const distFromCenter = Math.hypot(anchor.x - cx, anchor.y - cy);
    push = Math.max(push, minOut - distFromCenter + 48);
  }
  return { x: anchor.x + dx * push, y: anchor.y + dy * push };
}

/**
 * Веер вокруг центра стола: x_i = center + (i - (n-1)/2) * step, rotation в [-15°, 15°].
 * @param {{ x: number, y: number }} center
 * @param {number} n
 * @param {number} i
 */
function fanSlot(center, n, i) {
  const mid = (n - 1) / 2;
  const step = Math.min(52, 18 + n * 8);
  const t = n <= 1 ? 0 : i - mid;
  const x = center.x + t * step;
  const y = center.y + Math.abs(t) * 5;
  const maxRot = 15;
  const rot = n <= 1 || mid === 0 ? 0 : (t / mid) * maxRot;
  return { x, y, rot };
}

/** Широкий веер + лёгкий «крест» по Y для карэ (4 карты). */
function fanSlotCarre(center, i) {
  const offset = 62;
  const t = i - 1.5;
  const x = center.x + t * offset;
  const crossY = i % 2 === 0 ? 7 : -5;
  const y = center.y + Math.abs(t) * 4 + crossY;
  const rot = t * 11;
  return { x, y, rot };
}

/**
 * Узкий пучок у точки вылета (все комбо): линейный веер или мини-крест карэ при 4 картах.
 * @param {{ x: number, y: number }} origin
 * @param {number} n
 * @param {number} i
 * @param {boolean} carreLayout
 */
function comboTightClusterAt(origin, n, i, carreLayout) {
  if (carreLayout && n === 4) {
    const offset = 13;
    const t = i - 1.5;
    const x = origin.x + t * offset;
    const crossY = i % 2 === 0 ? 2 : -2;
    const y = origin.y + Math.abs(t) * 2 + crossY;
    const rot = t * 7;
    return { x, y, rot };
  }
  const mid = (n - 1) / 2;
  const step = n >= 4 ? 9 : 12;
  const t = n <= 1 ? 0 : i - mid;
  const x = origin.x + t * step;
  const y = origin.y + Math.abs(t) * 2.5;
  const maxRot = n >= 4 ? 8 : 10;
  const rot = n <= 1 || mid === 0 ? 0 : (t / mid) * maxRot;
  return { x, y, rot };
}

/**
 * @param {'normal' | 'catchup'} preset
 * @param {boolean} carreTier
 * @param {boolean} terzTier
 */
function revealTimings(preset, carreTier, terzTier) {
  const parallel = {
    flyStagger: 0,
    fadeStagger: 0,
    reducedStaggerIn: 0,
    reducedStaggerOut: 0,
  };
  if (preset === 'catchup') {
    const base = {
      ...parallel,
      flyDur: 0.28,
      fanDur: 0.26,
      hold: 0.12,
      fadeDur: 0.28,
      reducedIn: 0.12,
      reducedPause: 0.12,
      reducedOut: 0.12,
    };
    if (carreTier) {
      return {
        ...base,
        flyDur: 0.3,
        fanDur: 0.28,
        hold: 0.3,
        fadeDur: 0.3,
        reducedPause: 0.45,
      };
    }
    if (terzTier) {
      return {
        ...base,
        hold: 0.38,
        fadeDur: 0.34,
        reducedPause: 0.55,
      };
    }
    return base;
  }
  const base = {
    ...parallel,
    flyDur: 0.52,
    fanDur: 0.46,
    hold: 1.0,
    fadeDur: 0.45,
    reducedIn: 0.2,
    reducedPause: 0.55,
    reducedOut: 0.2,
  };
  if (carreTier) {
    return {
      ...base,
      flyDur: 0.5,
      fanDur: 0.46,
      hold: 1.78,
      fadeDur: 0.42,
      reducedPause: 0.85,
    };
  }
  if (terzTier) {
    return {
      ...base,
      flyDur: 0.58,
      fanDur: 0.52,
      hold: 2.0,
      fadeDur: 0.5,
      reducedPause: 1.0,
    };
  }
  return base;
}

/**
 * @param {{
 *   token: number,
 *   cardIds: number[],
 *   heroSeatAnchorRef: React.RefObject<HTMLElement|null>,
 *   launchAnchorRef?: React.RefObject<HTMLElement|null>|null,
 *   seatArenaRef: React.RefObject<HTMLElement|null>,
 *   label?: string,
 *   comboLabelRaw?: string,
 *   onDone: () => void,
 *   timingPreset?: 'normal' | 'catchup',
 * }} props
 */
function ComboAnnounceRevealPortal({
  token,
  cardIds,
  heroSeatAnchorRef,
  launchAnchorRef = null,
  seatArenaRef,
  label,
  comboLabelRaw,
  onDone,
  timingPreset = 'normal',
}) {
  const rootRef = useRef(null);
  const cardWrapRefs = useRef([]);
  const killedRef = useRef(false);

  useLayoutEffect(() => {
    killedRef.current = false;
    const ids = Array.isArray(cardIds) ? cardIds.filter((id) => Number.isInteger(id)) : [];
    const root = rootRef.current;
    if (ids.length === 0 || !root) {
      onDone();
      return undefined;
    }

    const wraps = cardWrapRefs.current.filter(Boolean);
    if (wraps.length !== ids.length) {
      onDone();
      return undefined;
    }

    const reduced = prefersReducedMotion();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const viewportCenter = { x: vw * 0.5, y: vh * 0.42 };

    const launchEl =
      (launchAnchorRef && launchAnchorRef.current) || heroSeatAnchorRef?.current || null;
    const arenaEl = seatArenaRef?.current ?? null;

    const heroFallback = { x: vw * 0.5, y: vh * 0.88 };
    const heroC = anchorCenter(launchEl, heroFallback);
    const fanCenter = anchorCenter(arenaEl, viewportCenter);

    const carreTier = isCarreRevealCandidate({
      comboLabelRaw,
      label,
      cardIds: ids,
    });
    const isTerzReveal = isTerzRevealCandidate({
      comboLabelRaw,
      label,
      cardIds: ids,
    });
    const start = reduced ? fanCenter : startLaunchPoint(heroC, { carre: carreTier, seatCombo: !carreTier });
    const n = ids.length;
    const tm = revealTimings(timingPreset, carreTier, isTerzReveal);

    const ctx = gsap.context(() => {
      if (reduced) {
        wraps.forEach((el) => {
          gsap.set(el, {
            left: fanCenter.x,
            top: fanCenter.y,
            xPercent: -50,
            yPercent: -50,
            opacity: 0,
            scale: 0.96,
            rotation: 0,
          });
        });
        const tl = gsap.timeline({
          onComplete: () => {
            if (!killedRef.current) onDone();
          },
        });
        tl.to(wraps, {
          opacity: 1,
          scale: 1,
          duration: tm.reducedIn,
          stagger: tm.reducedStaggerIn,
          ease: 'power1.out',
        });
        tl.to({}, { duration: tm.reducedPause });
        tl.to(wraps, {
          opacity: 0,
          scale: 0.94,
          duration: tm.reducedOut,
          stagger: tm.reducedStaggerOut,
          ease: 'power1.in',
        });
        return;
      }

      const tightCarre = carreTier && n === 4;
      wraps.forEach((el, i) => {
        const tight = comboTightClusterAt(start, n, i, tightCarre);
        gsap.set(el, {
          left: tight.x,
          top: tight.y,
          xPercent: -50,
          yPercent: -50,
          opacity: 0,
          scale: carreTier ? 0.58 : 0.62,
          rotation: tight.rot,
        });
      });

      const tl = gsap.timeline({
        onComplete: () => {
          if (!killedRef.current) onDone();
        },
      });

      const fanOverlap = timingPreset === 'catchup' ? '-=0.1' : '-=0.2';
      tl.to(wraps, {
        left: fanCenter.x,
        top: fanCenter.y,
        scale: 1,
        opacity: 1,
        duration: tm.flyDur,
        stagger: tm.flyStagger,
        ease: 'power3.out',
      });

      tl.add('fan', fanOverlap);
      if (root) {
        const peakGlow = carreTier
          ? 'inset 0 0 72px rgba(252, 211, 77, 0.55)'
          : 'inset 0 0 48px rgba(251, 191, 36, 0.38)';
        const glowInDur = carreTier
          ? timingPreset === 'catchup'
            ? 0.08
            : 0.12
          : timingPreset === 'catchup'
            ? 0.06
            : 0.1;
        tl.fromTo(
          root,
          { boxShadow: 'inset 0 0 0 rgba(251, 191, 36, 0)' },
          {
            boxShadow: peakGlow,
            duration: glowInDur,
            ease: 'power2.out',
          },
          'fan',
        );
        if (carreTier && !reduced) {
          tl.to(
            root,
            { x: '+=3.5', duration: 0.032, ease: 'power1.inOut', yoyo: true, repeat: 5 },
            'fan+=0.02',
          );
        }
        tl.to(
          root,
          {
            boxShadow: 'inset 0 0 0 rgba(0,0,0,0)',
            duration: carreTier ? 0.16 : timingPreset === 'catchup' ? 0.08 : 0.14,
            ease: 'power2.in',
          },
          carreTier ? 'fan+=0.1' : timingPreset === 'catchup' ? 'fan+=0.04' : 'fan+=0.08',
        );
        if (carreTier && !reduced) {
          tl.set(root, { clearProps: 'x' });
        }
      }
      wraps.forEach((el, i) => {
        const slot =
          carreTier && n === 4 ? fanSlotCarre(fanCenter, i) : fanSlot(fanCenter, n, i);
        tl.to(
          el,
          {
            left: slot.x,
            top: slot.y,
            rotation: slot.rot,
            duration: tm.fanDur,
            ease: 'power2.inOut',
          },
          'fan',
        );
      });

      tl.to({}, { duration: tm.hold });

      wraps.forEach((el, i) => {
        const back = comboTightClusterAt(start, n, i, tightCarre);
        tl.to(
          el,
          {
            left: back.x,
            top: back.y,
            opacity: 0,
            scale: carreTier ? 0.52 : 0.55,
            rotation: back.rot,
            duration: tm.fadeDur,
            ease: 'power2.inOut',
          },
          'comboOut',
        );
      });
    }, root);

    return () => {
      killedRef.current = true;
      ctx.revert();
    };
  }, [
    token,
    cardIds,
    heroSeatAnchorRef,
    launchAnchorRef,
    seatArenaRef,
    onDone,
    timingPreset,
    comboLabelRaw,
    label,
  ]);

  const ids = Array.isArray(cardIds) ? cardIds.filter((id) => Number.isInteger(id)) : [];
  cardWrapRefs.current = [];

  const chipText =
    revealChipTextFromOpts({ comboLabelRaw, label }) ||
    (typeof label === 'string' && label.trim() ? label.trim().slice(0, 8) : '');
  const carreTierUi = isCarreRevealCandidate({
    comboLabelRaw,
    label,
    cardIds: ids,
  });

  const node = (
    <div
      ref={rootRef}
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: REVEAL_Z }}
      aria-hidden={!chipText}
    >
      {chipText ? (
        <div
          className={
            carreTierUi
              ? 'absolute left-1/2 top-[11%] max-w-[90vw] -translate-x-1/2 rounded-xl border border-amber-300/60 bg-gradient-to-b from-amber-950/90 via-black/70 to-black/85 px-4 py-1.5 text-center font-mono text-sm font-black tracking-widest text-amber-50 shadow-[0_0_28px_rgba(251,191,36,0.55),inset_0_1px_0_rgba(255,255,255,0.12)] backdrop-blur-sm'
              : 'absolute left-1/2 top-[12%] max-w-[90vw] -translate-x-1/2 rounded-lg border border-amber-400/35 bg-black/55 px-3 py-1 text-center text-[11px] font-bold text-amber-100/95 shadow-lg backdrop-blur-sm'
          }
        >
          {chipText}
        </div>
      ) : null}
      {ids.map((cardId, i) => {
        const faceDown = !Number.isInteger(cardId) || cardId < 0;
        const { rank, suit } = faceDown ? { rank: '', suit: '' } : belotCardIdToPlaying(cardId);
        return (
          <div
            key={`${token}-${cardId}-${i}`}
            ref={(el) => {
              cardWrapRefs.current[i] = el;
            }}
            className="fixed h-0 w-0"
          >
            <PlayingCard
              rank={rank}
              suit={suit}
              faceDown={faceDown}
              size="md"
              className="shadow-2xl ring-1 ring-amber-300/40"
            />
          </div>
        );
      })}
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}

/**
 * GSAP-портал для вылета карт при «Объявить» (хардкор). Один активный прогон.
 *
 * @param {{
 *   heroSeatAnchorRef?: React.RefObject<HTMLElement|null>,
 *   seatArenaRef?: React.RefObject<HTMLElement|null>,
 * }} [options] — центр веера: центр `seatArenaRef`, иначе центр вьюпорта; онлайн можно передать ref центра взятки.
 * @returns {{
 *   playReveal: (opts: { cardIds: number[], label?: string }) => void,
 *   revealPortal: React.ReactNode,
 *   cancelAll: () => void,
 * }}
 */
export function useBelotComboAnnounceCardReveal(options = {}) {
  const nullRef = useRef(null);
  const heroSeatAnchorRef = options.heroSeatAnchorRef ?? nullRef;
  const seatArenaRef = options.seatArenaRef ?? nullRef;

  const [active, setActive] = useState(
    /** @type {null | { token: number, cardIds: number[], label?: string, comboLabelRaw?: string, launchAnchorRef?: React.RefObject<HTMLElement|null>|null, onComplete?: () => void, timingPreset?: 'normal' | 'catchup' }} */ (
      null
    ),
  );

  const handleRevealDone = useCallback(() => {
    setActive((cur) => {
      try {
        cur?.onComplete?.();
      } catch {
        /* ignore */
      }
      return null;
    });
  }, []);

  const playReveal = useCallback((opts) => {
    const cardIds = Array.isArray(opts?.cardIds) ? opts.cardIds : [];
    if (!cardIds.length) {
      try {
        opts?.onComplete?.();
      } catch {
        /* ignore */
      }
      return;
    }
    const tp = opts && typeof opts === 'object' && opts.timingPreset === 'catchup' ? 'catchup' : 'normal';
    setActive({
      token: Date.now(),
      cardIds,
      label: typeof opts?.label === 'string' ? opts.label : undefined,
      comboLabelRaw:
        opts && typeof opts === 'object' && typeof opts.comboLabelRaw === 'string'
          ? opts.comboLabelRaw
          : undefined,
      launchAnchorRef:
        opts && typeof opts === 'object' && opts.launchAnchorRef != null ? opts.launchAnchorRef : undefined,
      onComplete: typeof opts?.onComplete === 'function' ? opts.onComplete : undefined,
      timingPreset: tp,
    });
  }, []);

  const cancelAll = useCallback(() => {
    setActive(null);
  }, []);

  const revealPortal =
    active != null ? (
      <ComboAnnounceRevealPortal
        token={active.token}
        cardIds={active.cardIds}
        heroSeatAnchorRef={heroSeatAnchorRef}
        launchAnchorRef={active.launchAnchorRef ?? null}
        seatArenaRef={seatArenaRef}
        label={active.label}
        comboLabelRaw={active.comboLabelRaw}
        timingPreset={active.timingPreset === 'catchup' ? 'catchup' : 'normal'}
        onDone={handleRevealDone}
      />
    ) : null;

  return { playReveal, revealPortal, cancelAll };
}
